/**
 * useConfirm — imperative confirm dialog built on Shadcn's AlertDialog.
 *
 * Replaces window.confirm() everywhere in the app. The native browser
 * confirm gets aggressively suppressed by Safari/Chrome after a few uses
 * ("Suppress dialogues" / "Block this page from creating additional dialogs"),
 * which silently breaks delete/remove flows. This component is a normal DOM
 * modal so it can never be muted by the browser.
 *
 * Usage at any component level:
 *
 *   import { ConfirmProvider, useConfirm } from '@/components/ConfirmDialog';
 *
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: 'Delete this lead?',
 *     body: 'This cannot be undone.',
 *     confirmLabel: 'Delete',
 *     tone: 'danger',
 *   });
 *   if (!ok) return;
 *
 * Provider lives at the App root.
 */
import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState({});
  // Promise resolver kept in a ref so we can resolve when the user picks an option.
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setOpts({
        title: options.title || 'Are you sure?',
        body: options.body || '',
        confirmLabel: options.confirmLabel || 'Confirm',
        cancelLabel: options.cancelLabel || 'Cancel',
        tone: options.tone || 'default', // 'default' | 'danger' | 'primary'
      });
      setOpen(true);
    });
  }, []);

  const handle = (result) => {
    setOpen(false);
    const resolve = resolverRef.current;
    resolverRef.current = null;
    if (resolve) resolve(result);
  };

  const toneClasses = {
    danger:  'bg-red-600 hover:bg-red-700 text-white',
    primary: 'bg-[#7A0B7A] hover:bg-[#5a085a] text-white',
    default: 'bg-gray-900 hover:bg-gray-800 text-white',
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={open} onOpenChange={(o) => { if (!o) handle(false); }}>
        <AlertDialogContent className="bg-white" data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{opts.title}</AlertDialogTitle>
            {opts.body ? (
              <AlertDialogDescription className="whitespace-pre-line">
                {opts.body}
              </AlertDialogDescription>
            ) : null}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => handle(false)} data-testid="confirm-dialog-cancel">
              {opts.cancelLabel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handle(true)}
              className={toneClasses[opts.tone] || toneClasses.default}
              data-testid="confirm-dialog-confirm"
            >
              {opts.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Fallback so calling components never throw if the provider isn't mounted
    // (e.g. during isolated tests). Returns the native confirm for parity.
    return async (o) => window.confirm(o.body || o.title || 'Are you sure?');
  }
  return ctx;
}
